/*
 * Authentication and security.
 */
import _ from 'lodash';
import * as Sentry from '@sentry/vue';
import moment from 'moment';

import { STANDALONE } from 'assets/js/api/environment';

const state = {
  authUser: null,
  auth: false,
  authChecked: false,
  expires: false,
  logoutMessage: false,
  jwt: false,
  totpRecoveryCode: null,
  websocketReportedJwtFailure: false,
};

const actions = {
  // AuthDataReset
  AuthDataReset({ commit }, msg) {
    commit('DO_LOGOUT', msg);
  },

  // SetAuthData
  SetAuthData({ commit }, loginResp) {
    commit('DO_LOGIN', loginResp);
  },

  // SetAuthToken
  SetAuthToken({ commit }, payload) {
    commit('SET_AUTH_TOKEN', payload);
  },

  // SetAuthUser
  SetAuthUser({ commit }, payload) {
    commit('SET_AUTH_USER', payload);
    window?.umami?.identify({ email: payload?.email });
  },

  // SetTOTPRecoveryCode
  SetTOTPRecoveryCode({ commit }, payload) {
    commit('SET_TOTP_RECOVERY_CODE', payload);
  },

  // SetLogoutMessage
  SetLogoutMessage({ commit }, msg) {
    commit('SET_LOGOUT_MSG', msg);
  },

  // SetWsFailure
  SetWsFailure({ commit }, msg) {
    commit('SET_WS_FAILURE', msg);
  },
};

const getters = {
  DaysSinceAccountCreation: (stateArg) => {
    if (!_.get(stateArg, 'authUser.created', false)) return 0;
    const created = moment.utc(stateArg.authUser.created);
    const now = moment.utc();
    const diff = now.unix() - created.unix();
    return Math.floor(diff / (60 * 60 * 24));
  },

  DisplayNameIsDefault: (stateArg) => {
    try {
      const emailName = stateArg.authUser.email.split('@')[0];
      if (stateArg.authUser.display_name === emailName) return true;
      return false;
    } catch (err) {
      return false;
    }
  },
};

const mutations = {
  // DO_LOGIN
  DO_LOGIN(stateArg, loginResp) {
    const stateCopy = stateArg;
    stateCopy.auth = true;
    stateCopy.authChecked = true;
    stateCopy.jwt = loginResp.token;
    stateCopy.expires = loginResp.expires;
    stateCopy.logoutMessage = false;
  },

  // DO_LOGOUT
  DO_LOGOUT(stateArg, msg) {
    const stateCopy = stateArg;
    stateCopy.authUser = null;
    stateCopy.jwt = false;
    stateCopy.auth = false;
    stateCopy.expires = false;
    stateCopy.authChecked = false;
    stateCopy.logoutMessage = msg || false;
    stateCopy.totpRecoveryCode = null;
    stateCopy.websocketReportedJwtFailure = false;
  },

  // SET_LOGOUT_MSG
  SET_LOGOUT_MSG(stateArg, msg) {
    const stateCopy = stateArg;
    stateCopy.logoutMessage = msg || false;
  },

  // SET_AUTH_TOKEN
  SET_AUTH_TOKEN(stateArg, payload) {
    const stateCopy = stateArg;
    stateCopy.jwt = payload.token;
    stateCopy.expires = payload.expires;
  },

  // SET_AUTH_USER
  SET_AUTH_USER(stateArg, payload) {
    const stateCopy = stateArg;
    if (process.env.NODE_ENV !== 'development' && !STANDALONE) {
      Sentry.getCurrentScope().setUser({
        email: _.get(payload, 'email'),
        id: _.get(payload, 'user_id'),
      });
    }
    stateCopy.authUser = payload;
  },

  // SET_AUTH_USER NAME
  SET_AUTH_USER_NAME(stateArg, name) {
    const stateCopy = stateArg;
    stateCopy.authUser.display_name = name;
  },

  SET_TOTP_RECOVERY_CODE(stateArg, value) {
    const stateCopy = stateArg;
    stateCopy.totpRecoveryCode = value;
  },

  // SET_WS_FAILURE
  SET_WS_FAILURE(stateArg, value) {
    const stateCopy = stateArg;
    stateCopy.websocketReportedJwtFailure = value;
  },
};

export default { actions, getters, mutations, state };
